import { ElementRef } from '@angular/core'
import * as FileSaver from 'file-saver-es'
import { CellValue, Workbook } from 'exceljs'

export default function download(src: Blob | string, filename: string) {
  const url = 'string' == typeof src ? src : window.URL.createObjectURL(src)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

export function downloadBytes(byte: BlobPart, filename: string) {
  const blob = new Blob([byte], { type: 'application/vnd.ms-excel' })
  download(blob, filename)
}

export function base64ToArrayBuffer(base64: string) {
  const binaryString = window.atob(base64)
  const binaryLen = binaryString.length
  const bytes = new Uint8Array(binaryLen)
  for (let i = 0; i < binaryLen; i++) {
    const ascii = binaryString.charCodeAt(i)
    bytes[i] = ascii
  }
  return bytes
}

export interface ExcelTitleCell {
  id: string
  value?: string
  isImage?: boolean
  imgExtension?: 'jpeg' | 'png' | 'gif'
  mergeCells?: string[]
  font?: { size?: number; weight?: boolean }
  weight?: boolean
}

interface ExcelHeader {
  data: CellValue[]
  bgColor?: string
}

interface ExcelRowItem {
  data: Record<string, unknown> | unknown[]
  bgColor?: string
}

interface ExcelSection {
  title?: string
  header?: ExcelHeader
  data?: ExcelRowItem[]
}

interface ExcelExportData {
  // Legacy single-table fields (title rows may be conditionally absent)
  title?: (ExcelTitleCell[] | undefined)[]
  header?: ExcelHeader
  data?: ExcelRowItem[]

  // New multi-section fields
  multiSection?: boolean
  sections?: ExcelSection[]
}

export function exportExcel(excelData: ExcelExportData, fileName: string, useBorder?: boolean, columnsWidth?: { key: number; value: number }[], customFooter?: boolean) {
  //Create a workbook with a single worksheet
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet('Sheet1')
  worksheet.views = [{ rightToLeft: document.dir == 'rtl' }]

  excelData.title?.forEach((titleRow) => {
    titleRow?.forEach((titleCell) => {
      if (titleCell) {
        const selectedCell = worksheet.getCell(titleCell.id)
        if (!titleCell.isImage) {
          selectedCell.value = titleCell.value ?? null
          if (titleCell.mergeCells) {
            worksheet.mergeCells(titleCell.mergeCells[0], titleCell.mergeCells[1])
          }
          if (titleCell.font) {
            selectedCell.font = {
              name: 'David',
              size: titleCell.font.size,
              bold: titleCell.weight,
            }
          }
          selectedCell.style = {
            alignment: { horizontal: document.dir == 'rtl' ? 'right' : 'left' },
          }
        } else {
          //Add Image
          const myLogoImage = workbook.addImage({
            base64: titleCell.value ?? '',
            extension: titleCell.imgExtension ?? 'png',
          })
          worksheet.mergeCells(`${titleCell.mergeCells?.[0]}:${titleCell.mergeCells?.[1]}`)
          worksheet.addImage(myLogoImage, `${titleCell.mergeCells?.[0]}:${titleCell.mergeCells?.[1]}`)
        }
      }
    })
  })
  // Blank row after title
  worksheet.addRow([])

  // Helper to write a single section
  const writeSection = (title: string | undefined, header: ExcelHeader | undefined, data: ExcelRowItem[], multiSection: boolean) => {
    // 1) Render the title (optional)
    if (multiSection) {
      worksheet.addRow([title])
    }

    // 2) Create header row
    if (header?.data) {
      const headerRow = worksheet.addRow(header.data)
      headerRow.eachCell((cell, index) => {
        if (columnsWidth?.length) {
          const exist = columnsWidth?.find((v) => v.key == index)
          if (exist) {
            worksheet.columns[exist.key - 1].width = exist.value
          }
        }
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: header.bgColor || '949494' }, // fallback color
        }
        cell.font = {
          bold: true,
          color: { argb: 'FFFFFF' },
          size: 12,
        }
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        if (useBorder) {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          }
        }
      })
    }

    // 3) Create data rows
    data?.forEach((rowItem, index: number) => {
      if (rowItem && rowItem.data) {
        const row = worksheet.addRow(Object.values(rowItem.data) as CellValue[])
        row.eachCell((cell) => {
          if (rowItem.bgColor) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: rowItem.bgColor },
            }
          }
          cell.alignment = { vertical: 'middle', horizontal: 'center' }
          if (useBorder) {
            const isFooterRow = customFooter && (index == data.length - 1 || index == data.length - 2)
            if (!isFooterRow) {
              cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
              }
            }
          }
        })
      }
    })
    // Blank row at end of section
    worksheet.addRow([])
  }

  // If multiSection == true, loop over each section
  if (excelData.multiSection && excelData.sections && excelData.sections.length) {
    excelData.sections.forEach((section, _idx) => {
      writeSection(section.title, section.header, section.data || [], true)
    })
  } else {
    // Fallback: single table (the cell-matrix legacy title was already rendered above; writeSection ignores title when multiSection=false)
    writeSection(undefined, excelData.header, excelData.data || [], false)
  }

  // Finally, save the workbook
  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    FileSaver.saveAs(blob, fileName + '.xlsx')
  })
}

export function printContent(ref: ElementRef<HTMLElement>) {
  const printContents = ref.nativeElement.innerHTML

  // Get all stylesheets HTML
  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((element) => element.outerHTML)
    .join('\n')

  const popupWin = window.open('', '_blank')
  if (!popupWin) {
    return
  }
  popupWin.document.open()
  popupWin.document.write(`
    <html>
      <head>
			${styles}
      </head>
      <body onload="window.print();" style="margin:0"><div style="width:1120px">${printContents}</div></body>
    </html>
  `)
  popupWin.document.close()
}
